import React from 'react';
import { AuthBadRequestError } from '@creditas-lab/auth-sdk';
import { formatPhone } from '../../../utils/format-phone';
import { useStep, useWizard } from '../../../components/StepWizard';
import { useVerifyClaim } from '../../../hooks/mutations/useVerifyClaim';
import {
  useResendVerificationCode,
  ResendVerificationCodeResponse,
} from '../../../hooks/mutations/useResendVerificationCode';
import {
  dispatchGoBackEvent,
  dispatchInvalidCodeEvent,
  dispatchRedirectEvent,
  dispatchResendCodeErrorEvent,
  dispatchResendCodeEvent,
  dispatchValidCodeEvent,
  dispatchVerifyClaimErrorEvent,
} from '../../../utils/analyticsEvents';
import i18n from '../../../i18n';
import { UseAccessCodeStepResult } from './types';
import { useGetFlowValues } from '../useGetFlowValues';
import { FlowTypes } from '../enums/FlowTypes';
import { ClaimTypes } from '../enums/ClaimTypes';

const ClaimFormatterByType = {
  [ClaimTypes.CELLPHONE]: (phone = ''): string => formatPhone(phone),
  [ClaimTypes.EMAIL]: (email = ''): string => email,
};

const useAccessCodeStep = (flowType: FlowTypes): UseAccessCodeStepResult => {
  const { claimType, data, setData, callback, dispatchNextFlowEvent } = useGetFlowValues(flowType);
  const [accessCode, setAccessCode] = React.useState<string>('');
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [isValid, setIsValid] = React.useState<boolean>(false);
  const { hasNext } = useWizard();

  const { handlePrev, handleNext } = useStep({
    onNext: (): void => {
      if (callback && !hasNext) {
        dispatchRedirectEvent[claimType]();
        callback.current();
        return;
      }

      if (dispatchNextFlowEvent) {
        dispatchNextFlowEvent();
      }
    },
  });
  const verifyClaimMutation = useVerifyClaim({
    onSuccess: (): void => {
      dispatchValidCodeEvent[claimType]();
      handleNext();
    },
    onError: (error: Error): void => {
      if (error instanceof AuthBadRequestError) {
        dispatchInvalidCodeEvent[claimType]();
        setErrorMessage(i18n.t('flows:shared.access_code_step.validations.invalid_access_code'));
        return;
      }

      dispatchVerifyClaimErrorEvent(`${claimType} | ${error.message}`);
      setErrorMessage(i18n.t('flows:shared.access_code_step.validations.unexpected_error'));
    },
  });
  const resendCodeMutation = useResendVerificationCode({
    onSuccess: (responseData: ResendVerificationCodeResponse): void => {
      dispatchResendCodeEvent[claimType]();
      setData(responseData);
    },
    onError: (error: Error): void => {
      dispatchResendCodeErrorEvent(`${claimType} | ${error.message}`);
    },
  });

  const validateAccessCode = (value: string): void => {
    if (value && value.length === 6) {
      setIsValid(true);
      return;
    }

    setIsValid(false);
  };

  const handleInputChange = (value: string): void => {
    setAccessCode(value);
    setErrorMessage('');

    validateAccessCode(value);
  };

  const handleResendLinkClick = (): void => {
    resendCodeMutation.mutate(data.verificationTicket);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    dispatchGoBackEvent[claimType]();
    handlePrev(event);
  };

  const handleSubmit = (
    event?: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>,
  ): void => {
    if (event) {
      event.preventDefault();
    }

    verifyClaimMutation.mutate({
      code: accessCode,
      verificationTicket: data.verificationTicket || '',
    });
  };

  return {
    claim: ClaimFormatterByType[claimType](data.claim),
    accessCode,
    errorMessage,
    isValid,
    isLoading: verifyClaimMutation.isLoading,
    handleResendLinkClick,
    handleInputChange,
    handleBackButtonClick,
    handleSubmit,
  };
};

export { useAccessCodeStep };
